import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type ISampleState, shouldLoadSample } from "@/redux/modules/sample.js";
import type { IAppState } from "@/redux/reducer.js";
import { ENTITY_TYPE } from "@/settings/settings.ts";

import GenericDetailPageLayout from "../../core/navigation-panel/GenericDetailPageLayout.js";
import type { IRouteNavItemProps } from "../../core/navigation-panel/RouteNavItem.js";
import { SampleDataView } from "./SampleDataView.tsx";
import { SampleEventsView } from "./SampleEventsView.tsx";
import { SampleHistoryView } from "./SampleHistoryView.tsx";
import { SampleSummaryView } from "./SampleSummaryView.js";

export const SamplePage: FC = () => {
  const { sampleId } = useParams<"sampleId">();
  const { sample, isLoading } = useSelector<IAppState, ISampleState>((state) => state.sample);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadSample, sampleId);

  useEffect(() => {
    void load();
  }, [load]);

  const getNavItems = (): IRouteNavItemProps[] => {
    const dataDec = {
      disabled: isLoading || !sample,
      tooltip: "There are no attributes defined for this sample",
    };

    const historyDec = {
      disabled: isLoading || !sample || sample.history?.length === 0,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <SampleSummaryView sampleId={Number(sampleId)} />,
        default: true,
      },
      {
        label: "Events",
        route: "events",
        pageComponent: <SampleEventsView sampleId={Number(sampleId)} sample={sample} />,
      },
      {
        label: "Data",
        disabledTooltip: dataDec.tooltip,
        route: "data",
        disabled: dataDec.disabled,
        pageComponent: <SampleDataView />,
        isLoading,
      },
      {
        label: "History",
        route: "history",
        disabled: historyDec.disabled,
        pageComponent: <SampleHistoryView />,
        isLoading,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.SAMPLE}
      entityId={toSafeInt(sampleId)}
      getDisplayName={() => sample?.name}
      sidebarNavItems={getNavItems()}
      // isLoading={isLoading}
    />
  );
};
